import {
  PALETTES,
  getCurrentPalette,
  setPalette,
  onPaletteChange,
} from '@/lib/theme';

/**
 * Paintbrush dropdown that switches the active colour palette.
 * Pure CSS-vars only — does not affect typography or layout.
 */
export function createPalettePicker(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'rd-palette-picker';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'rd-palette-btn';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', 'Choose colour theme');
  btn.title = 'Choose colour theme';
  btn.innerHTML = `
    <svg viewBox="0 0 512 512" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M464 258.2c0 2.7-1 5.2-4.2 8c-3.8 3.1-10.1 5.8-17.8 5.8L344 272c-53 0-96 43-96 96c0 6.8 .7 13.4 2.1 19.8c3.3 15.7 10.2 31.1 14.4 40.6c.7 1.6 1.4 3 1.9 4.3c5 11.5 5.6 15.4 5.6 17.1c0 5.3-1.9 9.5-3.8 11.8c-.9 1.1-1.6 1.6-2 1.8c-.3 .2-.8 .3-1.6 .4c-2.9 .1-5.7 .2-8.6 .2C141.1 464 48 370.9 48 256S141.1 48 256 48s208 93.1 208 208c0 .7 0 1.4 0 2.2zm48 .5c0-.9 0-1.8 0-2.7C512 114.6 397.4 0 256 0S0 114.6 0 256S114.6 512 256 512c3.5 0 7.1-.1 10.6-.2c31.8-1.3 53.4-30.1 53.4-62c0-14.5-6.1-28.3-12.1-42c-4.3-9.8-8.7-19.7-10.8-29.9c-.7-3.2-1-6.5-1-9.9c0-26.5 21.5-48 48-48l97.9 0c36.5 0 69.7-24.8 70.1-61.3zM160 256a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-64a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm128-64a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm64 64a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
    </svg>
  `;

  const menu = document.createElement('div');
  menu.className = 'rd-palette-menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  function render(): void {
    const current = getCurrentPalette();
    menu.innerHTML = '';
    for (const p of PALETTES) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'rd-palette-item';
      item.setAttribute('role', 'menuitemradio');
      item.setAttribute('aria-checked', String(p.id === current));
      if (p.id === current) item.classList.add('rd-palette-item--active');
      item.innerHTML = `
        <span class="rd-palette-swatch" style="background:${p.swatch}"></span>
        <span class="rd-palette-name">${p.label}</span>
      `;
      item.addEventListener('click', () => {
        setPalette(p.id);
        close();
      });
      menu.appendChild(item);
    }
  }

  function open(): void {
    menu.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onKey);
  }
  function close(): void {
    menu.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('keydown', onKey);
  }
  function onDocClick(e: MouseEvent): void {
    if (!wrapper.contains(e.target as Node)) close();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu.hidden) open(); else close();
  });

  onPaletteChange(render);
  render();

  wrapper.appendChild(btn);
  wrapper.appendChild(menu);
  return wrapper;
}
